import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { registerResponsiveContract, withConformance, checkContrast, checkViewportOverflow, checkHeadingHierarchy, checkDuplicateVisibleText, checkTextClipped, checkInlineStyleOnLibStamp, checkUtilityClassOnLibStamp, normalizeSeverity } from '@norkron/web-conformance';
import { z } from 'zod';
import { createContext, memo, useMemo, useRef, useEffect, useState, useCallback, Children, useContext, isValidElement, useId } from 'react';
import { useTabs } from '@norkron/web-a11y';
import { useThemeVar } from '@norkron/web-themer/react';
import { RichTooltip } from '@norkron/web-tooltip';
import { jsxs, jsx } from 'react/jsx-runtime';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';

// src/error-codes.ts
var TABS_LIB = "@norkron/web-tabs";
var TABS_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-tabs");
var { warn, panic, reportInvalidInput } = createScopedErrors(TABS_LIB, TABS_TOKEN);
try {
  registerCodes(TABS_LIB, {
    TABS_INVALID_VALUE: {
      description: "Controlled `value` prop did not match any of the supplied tab specs. The active-tab indicator is hidden until the consumer corrects the value.",
      severity: "warning",
      docsAnchor: "web-tabs/invalid-value",
      exampleFix: "Ensure the controlled `value` matches one of the `tabs[].value` strings supplied to <Tabs>."
    },
    WEB_TABS_INVALID_INPUT: {
      description: "A public input to @norkron/web-tabs failed zod validation. Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE",
      exampleFix: "Match the per-export schema (TabsPropsSchema, TabPanelPropsSchema, TabPanelsPropsSchema, useTabUrlSyncInputSchema)."
    },
    TB_NO_ARIA_TABLIST: {
      description: "The primary tabs root (the <div class='tabs'>) has no descendant element exposing role='tablist'. Screen readers cannot reach the tab strip via the standard ARIA tabs pattern.",
      severity: "critical-but-non-throwing",
      kind: "contract",
      docsAnchor: "web-tabs/no-aria-tablist",
      exampleFix: "Render the inner strip with role='tablist' (or with the tablistProps spread from @norkron/web-a11y::useTabs)."
    },
    TB_SELFCHECK_INTERNAL_ERROR: {
      description: "The @norkron/web-tabs selfCheck encountered an unexpected exception while walking the subtree. Treated as a non-fatal advisory; the rule that threw is skipped.",
      severity: "info",
      docsAnchor: "web-tabs/selfcheck-internal-error",
      exampleFix: "Re-run with TABS_SELFCHECK_DEBUG=1 to capture the stack; file an issue on @norkron/web-tabs with the host DOM snippet."
    },
    TB_SELFCHECK_BUDGET_EXCEEDED: {
      description: "The @norkron/web-tabs selfCheck exceeded its per-tree time budget. Remaining rules are skipped; the report is partial.",
      severity: "info",
      docsAnchor: "web-tabs/selfcheck-budget-exceeded",
      exampleFix: "Pass a larger { budgetMs } via selfCheck options, or narrow the root passed to selfCheck so fewer nodes are walked."
    }
  });
} catch {
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var STR_TO_LOCAL_CATEGORY = {
  theme: "theme",
  stylesheet: "stylesheet",
  a11y: "a11y",
  layout: "layout",
  rendering: "layout",
  content: "i18n",
  i18n: "i18n",
  cardinality: "cardinality",
  identity: "identity",
  schema: "schema",
  flow: "flow",
  deprecation: "deprecation",
  perf: "perf"
};
function adaptStructural(libId, scope, checks) {
  const out = [];
  for (const fn of checks) {
    try {
      for (const f of fn(scope)) {
        out.push({
          libId,
          code: f.code,
          category: STR_TO_LOCAL_CATEGORY[f.category] ?? "layout",
          severity: normalizeSeverity(f.severity),
          message: f.message,
          selector: f.selector,
          suggestedFix: f.suggestedFix,
          extras: f.extras
        });
      }
    } catch {
    }
  }
  return out;
}
var LIB_ID = "@norkron/web-tabs";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}
function selfCheck(root, opts) {
  const start = now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-tabs"]')
    );
    if (instances.length === 0) return findings;
    const PANEL_CLASS_RE = /(?:^|\s)(?:tabs__panels?(?:--[\w-]+)?)(?:\s|$)/;
    const PANEL_ROLE_SET = /* @__PURE__ */ new Set(["tabpanel"]);
    const primaryRoots = instances.filter((el) => {
      const role = el.getAttribute("role");
      if (role && PANEL_ROLE_SET.has(role)) return false;
      const cls = el.getAttribute("class") ?? "";
      if (PANEL_CLASS_RE.test(cls)) return false;
      return true;
    });
    for (const inst of primaryRoots) {
      const role = inst.getAttribute("role");
      const innerTablist = inst.querySelector('[role="tablist"]');
      if (role !== "tablist" && !innerTablist) {
        findings.push({
          libId: LIB_ID,
          code: "TB_NO_ARIA_TABLIST",
          category: "a11y",
          severity: "major",
          message: 'Tabs root has no `role="tablist"` (neither self nor descendant). Screen readers cannot expose the tab semantics.',
          selector: selectorFor(inst),
          suggestedFix: 'Add `role="tablist"` to the tabs container.'
        });
      }
    }
    for (const inst of instances) {
      const helperScope = inst;
      findings.push(
        ...adaptStructural(LIB_ID, helperScope, [
          checkContrast,
          checkViewportOverflow,
          checkHeadingHierarchy,
          checkDuplicateVisibleText,
          checkTextClipped,
          checkInlineStyleOnLibStamp,
          checkUtilityClassOnLibStamp
        ])
      );
    }
    void instances;
    void selectorFor;
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "TB_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  if (now() - start > safeOpts.budgetMs) {
    findings.push({
      libId: LIB_ID,
      code: "TB_SELFCHECK_BUDGET_EXCEEDED",
      category: "perf",
      severity: "info",
      message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms.`,
      extras: { budgetMs: safeOpts.budgetMs, elapsed: now() - start }
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.7.4";
var TabsVariantSchema = z.enum(["underline", "pills", "enclosed"]);
var TabsSizeSchema = z.enum(["sm", "md", "lg"]);
var TabsAlignSchema = z.enum(["start", "center", "end"]);
var TabsOrientationSchema = z.enum(["horizontal", "vertical"]);
var TabSpecSchema = z.object({
  value: z.string().min(1),
  label: z.unknown(),
  icon: z.unknown().optional(),
  badge: z.unknown().optional(),
  disabled: z.boolean().optional(),
  hidden: z.boolean().optional(),
  testId: z.string().optional(),
  tone: z.enum(["default", "danger", "warning"]).optional(),
  helpText: z.unknown().optional()
});
var TabsPropsSchema = z.object({
  tabs: z.array(TabSpecSchema).min(1),
  value: z.string(),
  onChange: z.unknown().refine((v) => typeof v === "function", { message: "expected function" }),
  urlParam: z.string().optional(),
  defaultValue: z.string().optional(),
  urlAdapter: z.unknown().optional(),
  variant: TabsVariantSchema.optional(),
  size: TabsSizeSchema.optional(),
  fullWidth: z.boolean().optional(),
  fitted: z.boolean().optional(),
  align: TabsAlignSchema.optional(),
  scrollable: z.boolean().optional(),
  orientation: TabsOrientationSchema.optional(),
  ariaLabel: z.string().optional(),
  testId: z.string().optional(),
  className: z.string().optional(),
  idPrefix: z.string().optional(),
  manualActivation: z.boolean().optional(),
  activationMode: z.enum(["automatic", "manual"]).optional(),
  loop: z.boolean().optional()
});
var TabPanelPropsSchema = z.object({
  value: z.string().min(1),
  children: z.unknown(),
  className: z.string().optional(),
  testId: z.string().optional(),
  forceMount: z.boolean().optional()
});
var TabPanelsPropsSchema = z.object({
  value: z.string(),
  idPrefix: z.string().optional(),
  lazy: z.union([z.boolean(), z.enum(["never", "on-first-visit", "always"])]).optional(),
  className: z.string().optional(),
  testId: z.string().optional(),
  children: z.unknown()
});
var TabUrlSyncAdapterSchema = z.object({
  read: z.unknown().refine((v) => typeof v === "function", { message: "expected function" }),
  write: z.unknown().refine((v) => typeof v === "function", { message: "expected function" })
});
var useTabUrlSyncInputSchema = z.object({
  urlParam: z.string().min(1),
  defaultValue: z.string(),
  validValues: z.array(z.string()).optional(),
  adapter: TabUrlSyncAdapterSchema.optional()
});

// src/zod-bridge.ts
function formatReceived(v) {
  if (typeof v === "string") return JSON.stringify(v);
  if (v === null) return "null";
  if (typeof v === "undefined") return "undefined";
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint")
    return String(v);
  if (Array.isArray(v)) return "array";
  if (typeof v === "function") return "function";
  if (typeof v === "object") return "object";
  return typeof v;
}
function unitFor(type) {
  switch (type) {
    case "array":
      return "element(s)";
    case "string":
      return "character(s)";
    case "set":
      return "item(s)";
    default:
      return type ?? "value(s)";
  }
}
function enrichExpected(issue) {
  const received = issue.received;
  const hasReceived = "received" in issue && typeof received !== "undefined";
  switch (issue.code) {
    case "invalid_enum_value": {
      const literals = (issue.options ?? []).map((o) => JSON.stringify(o)).join(" | ");
      const base = literals.length > 0 ? `one of ${literals}` : "a valid enum value";
      return hasReceived ? `${base}, received ${formatReceived(received)}` : base;
    }
    case "invalid_type": {
      const want = issue.expected ?? "valid input";
      const recv = typeof received === "string" ? received : formatReceived(received);
      return hasReceived ? `${want}, received ${recv}` : want;
    }
    case "too_small": {
      const unit = unitFor(issue.type);
      const base = `at least ${issue.minimum ?? "?"} ${unit}`;
      return hasReceived ? `${base}, received ${formatReceived(received)}` : base;
    }
    case "too_big": {
      const unit = unitFor(issue.type);
      const base = `at most ${issue.maximum ?? "?"} ${unit}`;
      return hasReceived ? `${base}, received ${formatReceived(received)}` : base;
    }
    case "invalid_string": {
      const base = `a valid ${issue.validation ?? "string"}`;
      return hasReceived ? `${base}, received ${formatReceived(received)}` : base;
    }
    case "custom": {
      const base = issue.message && issue.message.length > 0 ? issue.message : "valid input";
      return hasReceived ? `${base}, received ${formatReceived(received)}` : base;
    }
    default: {
      const base = typeof issue.expected === "string" && issue.expected.length > 0 ? issue.expected : issue.message ?? issue.code ?? "valid input";
      return hasReceived ? `${base}, received ${formatReceived(received)}` : String(base);
    }
  }
}
function enrichIssue(issue) {
  const path = issue.path ?? [];
  const expected = enrichExpected(issue);
  const out = {
    path,
    message: issue.message ?? "",
    expected
  };
  if (typeof issue.code === "string") out.code = issue.code;
  if ("received" in issue) out.received = issue.received;
  return out;
}
function toZodLikeSafeParseError(failure) {
  const enriched = failure.error.issues.map((i) => enrichIssue(i));
  return { success: false, error: { issues: enriched } };
}

// src/validate-input.ts
function validateTabsProps(props) {
  const parsed = TabsPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: TABS_LIB,
      libToken: TABS_TOKEN,
      fn: "Tabs",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateTabPanelProps(props) {
  const parsed = TabPanelPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: TABS_LIB,
      libToken: TABS_TOKEN,
      fn: "TabPanel",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateTabPanelsProps(props) {
  const parsed = TabPanelsPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: TABS_LIB,
      libToken: TABS_TOKEN,
      fn: "TabPanels",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateUseTabUrlSyncInput(input) {
  const parsed = useTabUrlSyncInputSchema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: TABS_LIB,
      libToken: TABS_TOKEN,
      fn: "useTabUrlSync",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/use-tab-url-sync.ts
var isBrowser = () => typeof window !== "undefined" && typeof window.history !== "undefined";
var defaultTabUrlSyncAdapter = {
  read: (param) => {
    if (!isBrowser()) return null;
    return new URLSearchParams(window.location.search).get(param);
  },
  write: (param, value) => {
    if (!isBrowser()) return;
    const params = new URLSearchParams(window.location.search);
    if (value) params.set(param, value);
    else params.delete(param);
    const search = params.toString();
    const url = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    window.history.pushState(null, "", url);
  }
};
function useTabUrlSync(urlParam, defaultValue, validValues, adapter = defaultTabUrlSyncAdapter) {
  validateUseTabUrlSyncInput({ urlParam, defaultValue, validValues, adapter });
  const isValid = useCallback(
    (v) => {
      if (v === null) return false;
      if (!validValues) return true;
      return validValues.includes(v);
    },
    [validValues]
  );
  const [value, setValueState] = useState(() => {
    const fromUrl = adapter.read(urlParam);
    return isValid(fromUrl) ? fromUrl : defaultValue;
  });
  const setValue = useCallback(
    (next) => {
      setValueState(next);
      adapter.write(urlParam, next);
    },
    [adapter, urlParam]
  );
  useEffect(() => {
    if (!isBrowser()) return;
    const handler = () => {
      const fromUrl = adapter.read(urlParam);
      setValueState(isValid(fromUrl) ? fromUrl : defaultValue);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [adapter, urlParam, defaultValue, isValid]);
  return [value, setValue];
}
var TabsRootContext = createContext(null);
function useTabsRootContext() {
  return useContext(TabsRootContext);
}
function TabsRoot(props) {
  const { idPrefix, children } = props;
  const reactId = useId();
  const panelsRef = useRef(/* @__PURE__ */ new Set());
  const [version, bump] = useState(0);
  const registerPanel = useCallback((panelValue) => {
    panelsRef.current.add(panelValue);
    bump((n) => n + 1);
    return () => {
      panelsRef.current.delete(panelValue);
      bump((n) => n + 1);
    };
  }, []);
  const hasPanel = useCallback(
    (panelValue) => panelsRef.current.has(panelValue),
    [version]
  );
  const resolvedIdPrefix = idPrefix ?? `t${reactId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const value = useMemo(
    () => ({
      idPrefix: resolvedIdPrefix,
      registerPanel,
      hasPanel
    }),
    [resolvedIdPrefix, registerPanel, hasPanel]
  );
  return /* @__PURE__ */ jsx(TabsRootContext.Provider, { value, children });
}
TabsRoot.displayName = "TabsRoot";
function getVisibleTabs(tabs) {
  return tabs.filter((t) => !t.hidden);
}
function classes(...parts) {
  return parts.filter(Boolean).join(" ");
}
function listModifiers(variant, size, align, orientation, fullWidth, scrollable, fitted) {
  return classes(
    "tabs__list",
    `tabs__list--${variant}`,
    `tabs__list--${size}`,
    `tabs__list--align-${align}`,
    `tabs__list--${orientation}`,
    fullWidth && "tabs__list--full-width",
    scrollable && "tabs__list--scrollable",
    fitted && "tabs__list--fitted"
  );
}
function tabModifiers(variant, size, tone, active, disabled, fullWidth) {
  return classes(
    "tabs__tab",
    `tabs__tab--${variant}`,
    `tabs__tab--${size}`,
    `tabs__tab--tone-${tone}`,
    active && "tabs__tab--active",
    disabled && "tabs__tab--disabled",
    fullWidth && "tabs__tab--full-width"
  );
}
function TabsImpl(props) {
  validateTabsProps(props);
  const {
    tabs,
    value,
    onChange,
    urlParam,
    defaultValue,
    urlAdapter = defaultTabUrlSyncAdapter,
    variant = "underline",
    size = "md",
    fullWidth = false,
    fitted = false,
    align = "start",
    scrollable = false,
    orientation = "horizontal",
    ariaLabel,
    testId,
    className,
    idPrefix,
    manualActivation = false,
    activationMode,
    loop = true
  } = props;
  const rootCtx = useTabsRootContext();
  const effectiveIdPrefix = idPrefix ?? rootCtx?.idPrefix;
  const effectiveManual = activationMode !== void 0 ? activationMode === "manual" : manualActivation;
  const visibleTabs = useMemo(() => getVisibleTabs(tabs), [tabs]);
  const prevVisibleTabsRef = useRef(visibleTabs);
  useEffect(() => {
    const prev = prevVisibleTabsRef.current;
    prevVisibleTabsRef.current = visibleTabs;
    if (visibleTabs.length === 0) return;
    const stillPresent = visibleTabs.some(
      (t) => t.value === value && !t.disabled
    );
    if (stillPresent) return;
    const priorIdx = prev.findIndex((t) => t.value === value);
    const enabled = visibleTabs.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const targetIdx = priorIdx === -1 ? 0 : Math.min(priorIdx, enabled.length - 1);
    const next = enabled[targetIdx] ?? enabled[0];
    if (next && next.value !== value) onChange(next.value);
  }, [visibleTabs, value, onChange]);
  const stripRef = useRef(null);
  const tabRefs = useRef(/* @__PURE__ */ new Map());
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const valid = visibleTabs.some((t) => t.value === value);
    if (!valid) {
      warn({
        lib: "@norkron/web-tabs",
        libToken: TABS_TOKEN,
        fn: "Tabs",
        code: "TABS_INVALID_VALUE",
        message: `Controlled value "${String(value)}" does not match any visible, non-disabled tab. Tabs will render with no active panel.`,
        severity: "warning",
        dedupKey: `tabs-invalid-value:${String(value)}`,
        received: value,
        expected: { kind: "enum", valid: visibleTabs.map((t) => t.value) }
      });
    }
  }, [value, visibleTabs]);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const activeIndicatorColor = useThemeVar("--tabs__tab--active-indicator-color", "");
  const focusRingColor = useThemeVar("--tabs-focus-ring", "");
  const themeStyle = useMemo(() => {
    const s = {};
    if (activeIndicatorColor) s["--tabs__tab--active-indicator-color"] = activeIndicatorColor;
    if (focusRingColor) s["--tabs-focus-ring"] = focusRingColor;
    return s;
  }, [activeIndicatorColor, focusRingColor]);
  const a11yTabs = useMemo(
    () => visibleTabs.map((t) => ({ id: t.value, disabled: t.disabled })),
    [visibleTabs]
  );
  const { tablistProps, getTabProps, focusedIndex } = useTabs({
    tabs: a11yTabs,
    value,
    onChange: (id) => onChange(id),
    orientation,
    manualActivation: effectiveManual,
    idPrefix: effectiveIdPrefix
  });
  useEffect(() => {
    if (!effectiveManual) return;
    const tab = visibleTabs[focusedIndex];
    if (!tab) return;
    const el = tabRefs.current.get(tab.value);
    if (!el) return;
    if (document.activeElement === el) return;
    const activeInTablist = stripRef.current?.contains(document.activeElement);
    if (!activeInTablist) return;
    el.focus();
  }, [effectiveManual, focusedIndex, visibleTabs]);
  useEffect(() => {
    if (!urlParam) return;
    const fromUrl = urlAdapter.read(urlParam);
    if (fromUrl) {
      const valid = visibleTabs.find((t) => t.value === fromUrl && !t.disabled);
      if (valid && valid.value !== value) {
        onChange(valid.value);
        return;
      }
    }
    const initial = defaultValue ?? value;
    if (!fromUrl && initial) urlAdapter.write(urlParam, initial);
  }, [urlParam]);
  useEffect(() => {
    if (!urlParam) return;
    const current = urlAdapter.read(urlParam);
    if (current !== value) urlAdapter.write(urlParam, value);
  }, [urlParam, urlAdapter, value]);
  useEffect(() => {
    if (!urlParam) return;
    const handler = () => {
      const fromUrl = urlAdapter.read(urlParam);
      if (fromUrl && fromUrl !== value) {
        const valid = visibleTabs.find((t) => t.value === fromUrl && !t.disabled);
        if (valid) onChange(valid.value);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [urlParam, urlAdapter, value, visibleTabs, onChange]);
  const updateScrollIndicators = useCallback(() => {
    const el = stripRef.current;
    if (!el || !scrollable) return;
    setShowLeftScroll(el.scrollLeft > 4);
    setShowRightScroll(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, [scrollable]);
  useEffect(() => {
    if (!scrollable) return;
    updateScrollIndicators();
    const el = stripRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollIndicators, { passive: true });
    window.addEventListener("resize", updateScrollIndicators);
    return () => {
      el.removeEventListener("scroll", updateScrollIndicators);
      window.removeEventListener("resize", updateScrollIndicators);
    };
  }, [scrollable, updateScrollIndicators, visibleTabs.length]);
  const scrollBy = useCallback((direction) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(120, el.clientWidth * 0.6),
      behavior: "smooth"
    });
  }, []);
  const renderBadge = (badge) => {
    if (badge === void 0 || badge === null) return null;
    return /* @__PURE__ */ jsx("span", { className: "tabs__badge", children: badge });
  };
  const rootClass = classes(
    "tabs",
    `tabs--${orientation}`,
    scrollable && "tabs--scrollable",
    className
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...withConformance("@norkron/web-tabs", LIB_VERSION),
      className: rootClass,
      "data-testid": testId,
      style: themeStyle,
      children: [
        scrollable && showLeftScroll && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "Scroll tabs left",
            onClick: () => scrollBy(-1),
            "data-testid": testId ? `${testId}__scroll-left` : "tabs__scroll-left",
            className: "tabs__scroll-button tabs__scroll-button--left",
            children: "\u2039"
          }
        ),
        scrollable && showRightScroll && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "Scroll tabs right",
            onClick: () => scrollBy(1),
            "data-testid": testId ? `${testId}__scroll-right` : "tabs__scroll-right",
            className: "tabs__scroll-button tabs__scroll-button--right",
            children: "\u203A"
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: stripRef,
            ...tablistProps,
            "aria-label": ariaLabel,
            "data-keep-clear": "true",
            "data-allow-wide-gap": scrollable ? "true" : void 0,
            className: listModifiers(variant, size, align, orientation, fullWidth, scrollable, fitted),
            children: visibleTabs.map((tab, idx) => {
              const rawA11y = getTabProps(tab.value);
              const panelExists = rootCtx?.hasPanel ? rootCtx.hasPanel(tab.value) : false;
              const a11y = panelExists ? rawA11y : (() => {
                const { "aria-controls": _ignored, ...rest } = rawA11y;
                return rest;
              })();
              const active = a11y["aria-selected"];
              const tone = tab.tone ?? "default";
              const tid = tab.testId ?? `tabs__tab--${tab.value}`;
              const hookOnKeyDown = a11y.onKeyDown;
              const PREV_KEY = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
              const NEXT_KEY = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
              const firstEnabledIdx = visibleTabs.findIndex((t) => !t.disabled);
              let lastEnabledIdx = -1;
              for (let i = visibleTabs.length - 1; i >= 0; i--) {
                if (!visibleTabs[i]?.disabled) {
                  lastEnabledIdx = i;
                  break;
                }
              }
              const onKeyDown = (e) => {
                if (!loop) {
                  if (e.key === NEXT_KEY && idx === lastEnabledIdx) {
                    e.preventDefault();
                    return;
                  }
                  if (e.key === PREV_KEY && idx === firstEnabledIdx) {
                    e.preventDefault();
                    return;
                  }
                }
                hookOnKeyDown?.(e);
              };
              const a11yWithLoop = { ...a11y, onKeyDown };
              const button = /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  ref: (el) => {
                    if (el) tabRefs.current.set(tab.value, el);
                    else tabRefs.current.delete(tab.value);
                  },
                  ...a11yWithLoop,
                  disabled: tab.disabled,
                  "data-testid": tid,
                  "data-allow-corner-mismatch": "true",
                  className: tabModifiers(variant, size, tone, active, !!tab.disabled, fullWidth),
                  children: [
                    tab.icon && /* @__PURE__ */ jsx("span", { className: "tabs__icon", "aria-hidden": "true", children: tab.icon }),
                    /* @__PURE__ */ jsx("span", { className: "tabs__label", children: tab.label }),
                    renderBadge(tab.badge),
                    variant === "underline" && active && tone !== "default" && /* @__PURE__ */ jsx(
                      "span",
                      {
                        "aria-hidden": "true",
                        className: `tabs__tone-bar tabs__tone-bar--${tone}`
                      }
                    )
                  ]
                },
                tab.value
              );
              if (tab.helpText) {
                return /* @__PURE__ */ jsx(RichTooltip, { trigger: button, content: tab.helpText }, tab.value);
              }
              return button;
            })
          }
        )
      ]
    }
  );
}
var Tabs = memo(TabsImpl);
function TabPanel(props) {
  validateTabPanelProps(props);
  return null;
}
TabPanel.displayName = "TabPanel";
function isTabPanel(node) {
  return isValidElement(node) && (node.type === TabPanel || typeof node.type === "function" && node.type.displayName === "TabPanel");
}
function TabPanelsImpl(props) {
  validateTabPanelsProps(props);
  const {
    value,
    idPrefix,
    lazy = true,
    className,
    testId,
    children
  } = props;
  const rootCtx = useTabsRootContext();
  const effectiveIdPrefix = idPrefix ?? rootCtx?.idPrefix;
  const lazyMode = lazy === true ? "on-first-visit" : lazy === false ? "never" : lazy;
  const mountedSet = useRef(/* @__PURE__ */ new Set());
  mountedSet.current.add(value);
  useEffect(() => {
    mountedSet.current.add(value);
  }, [value]);
  const panels = [];
  Children.forEach(children, (child) => {
    if (isTabPanel(child)) panels.push(child);
  });
  const registerKey = panels.map((p) => String(p.props.value)).join("\0");
  const registerFn = rootCtx?.registerPanel;
  useEffect(() => {
    if (!registerFn) return;
    const values = registerKey ? registerKey.split("\0") : [];
    const unregs = values.map((v) => registerFn(v));
    return () => {
      for (const u of unregs) u();
    };
  }, [registerFn, registerKey]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ...withConformance("@norkron/web-tabs", LIB_VERSION),
      className: ["tabs__panels", className].filter(Boolean).join(" "),
      "data-testid": testId,
      "data-allow-shrink": "true",
      children: panels.map((panel) => {
        const panelValue = panel.props.value;
        const active = panelValue === value;
        const ever = mountedSet.current.has(panelValue);
        const forceMount = panel.props.forceMount === true;
        const shouldRender = forceMount ? true : lazyMode === "never" ? true : lazyMode === "always" ? active : active || ever;
        const panelId = effectiveIdPrefix ? `${effectiveIdPrefix}-panel-${panelValue}` : void 0;
        const labelledBy = effectiveIdPrefix ? `${effectiveIdPrefix}-tab-${panelValue}` : void 0;
        const tid = panel.props.testId ?? `tabs__panel--${panelValue}`;
        const panelClass = [
          "tabs__panel",
          active && "tabs__panel--active",
          panel.props.className
        ].filter(Boolean).join(" ");
        return /* @__PURE__ */ jsx(
          "div",
          {
            ...withConformance("@norkron/web-tabs", LIB_VERSION),
            id: panelId,
            role: "tabpanel",
            "aria-labelledby": labelledBy,
            hidden: !active,
            "aria-hidden": !active ? true : void 0,
            tabIndex: 0,
            "data-testid": tid,
            "data-active": active ? "true" : "false",
            "data-state": active ? "active" : "inactive",
            className: panelClass,
            children: shouldRender ? /* @__PURE__ */ jsx(
              ErrorBoundary,
              {
                fallback: (error, reset) => /* @__PURE__ */ jsx(
                  DefaultErrorFallback,
                  {
                    error,
                    reset,
                    title: `Panel "${String(panelValue)}" failed to render`
                  }
                ),
                children: panel.props.children
              }
            ) : null
          },
          panelValue
        );
      })
    }
  );
}
var TabPanels = memo(TabPanelsImpl);
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-tabs",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in: no .css, no @media/@container/matchMedia in tabs.tsx /
    // tab-panels.tsx. orientation is a prop, not a tier. Honest baseline:
    // 'full' at every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-tabs",
  () => selfCheck
);

export { LIB_VERSION, RESPONSIVE_CONTRACT, TabPanel, TabPanels, Tabs, TabsRoot, defaultTabUrlSyncAdapter, selfCheck, useTabUrlSync, useTabsRootContext };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map